import { ConditionalLink } from "components/conditionalLink";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { ArrowUpRight12 } from "components/icons/ArrowUpRight12";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { Form, Formik } from "formik";
import { manuallyTransactionsSchema } from "formValidations/manuallyTransactionsSchema";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import randomBytes from "randombytes";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import {
  useEditBankAccountMutation,
  useGetEntityBanksQuery,
} from "store/apis/bankConnections";
import { useAddManualTransactionsMutation } from "store/apis/transactions";
import { FileObject } from "types/Models/fileObject";
import { CSVResponse } from "types/Models/reconciliation";
import { BackendError } from "types/utils/error";
import { TransactionTable } from "./TransactionTable";
dayjs.extend(isSameOrBefore);

export type InternalTransaction = {
  id: number;
  date: string;
  merchant: string;
  category: string;
  description: string;
  amount: number;
  original_amount?: number;
  exchange_rate?: number;
  invoice: (FileObject & { file_data: FileObject | null }) | null;
  linked_transaction?: string;
  uuid?: string;
  classification_ids?: string[];
  // Which single bill a Withheld-tax-payable split line's `amount` is owed
  // against. To bifurcate withholding across multiple bills, the user adds
  // one such line per bill (via "Add more"), each with its own amount.
  withheldBillId?: string;
};

type TransactionForTable = Omit<InternalTransaction, "id"> & { id: string };

export const AddTransactionManuallyModal = ({
  onBack,
  close,
  selectedAccountId,
  csvTransactions,
  defaultTransactions,
  currency = "USD",
}: {
  onBack?: () => void;
  close: () => void;
  selectedAccountId: string;
  csvTransactions?: CSVResponse[];
  currency?: string;
  defaultTransactions?: (Omit<TransactionForTable, "invoice"> & {
    invoice: null;
  })[];
}) => {
  const {
    alertToast,
    successToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const isCSVTransactions = Boolean(csvTransactions);
  const { link } = useConstructInternalLink();

  const { data: lockDateData } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const { data: banks } = useGetEntityBanksQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId }
  );

  const [addManualTransactions] = useAddManualTransactionsMutation();
  const [editBankAccount] = useEditBankAccountMutation();

  const isNonUSD = currency !== "USD";

  const onSubmit = async ({
    transactions,
    reverse,
  }: {
    transactions: TransactionForTable[];
    reverse: boolean | null;
  }) => {
    const loadingtoastId = loadingToast({
      title: "Please wait",
      message: "Populating transactions...",
    });
    try {
      const selectedBank = banks?.accounts?.find(
        ({ account: { uuid } }) => uuid === selectedAccountId
      );
      const bankItem = banks?.items?.find(
        (item) => item.bank_brand.name === selectedBank?.bank_brand?.name
      );

      if (currency && !selectedBank?.account?.currency && bankItem) {
        await editBankAccount({
          bankAccountId: selectedAccountId,
          bankId: bankItem.uuid,
          groupId,
          entityId,
          payload: { currency },
        }).unwrap();
      }

      const transactionList = transactions
        .filter(
          ({ date, description }) => Boolean(date) && Boolean(description)
        )
        .map(
          ({
            amount,
            original_amount,
            exchange_rate,
            category,
            date,
            description,
            invoice,
            merchant,
          }) => {
            const signedAmount = reverse ? -1 * amount! : amount;
            const signedOriginal =
              original_amount !== undefined
                ? reverse
                  ? -1 * original_amount
                  : original_amount
                : undefined;

            return {
              date: dayjs(date).format(YYYY_MM_DD),
              amount: isNonUSD ? signedAmount : signedOriginal ?? signedAmount,
              original_amount: signedOriginal ?? signedAmount,
              exchange_rate,
              currency,
              description,
              invoice_id: invoice?.uuid! || null,
              merchant_data_id: merchant || "",
              transaction_category_id: category || "",
            };
          }
        );

      await addManualTransactions({
        accountId: selectedAccountId,
        entityId,
        groupId,
        transactions: transactionList,
      }).unwrap();

      successToast({ message: "Transactions have been populated!" });
      close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    } finally {
      dismissToast(loadingtoastId);
    }
  };

  let initialTransactions = csvTransactions
    ? csvTransactions.map((transaction) => ({
        id: randomBytes(16).toString("hex"),
        date: transaction.date,
        merchant: transaction.merchant || "",
        category: transaction.category || "",
        description: transaction.description || "",
        amount: transaction.amount ?? transaction.original_amount ?? 0,
        original_amount: transaction.original_amount ?? transaction.amount,
        exchange_rate: transaction.exchange_rate,
        invoice: null,
      }))
    : Array.from({ length: 3 }).map(() => ({
        id: randomBytes(16).toString("hex"),
        date: "",
        merchant: "",
        category: "",
        description: "",
        amount: 0,
        original_amount: undefined as number | undefined,
        exchange_rate: undefined as number | undefined,
        invoice: null,
      }));

  if (defaultTransactions && defaultTransactions.length > 0) {
    initialTransactions = defaultTransactions.map((t) => ({
      ...t,
      original_amount: undefined,
      exchange_rate: undefined,
    }));
  }

  const isTransactionFilled = (transaction: TransactionForTable) =>
    Boolean(
      transaction.date || transaction.amount !== 0 || transaction.description
    );

  const areMandatoryFieldsFilled = (transaction: TransactionForTable) =>
    Boolean(transaction.date && transaction.description);

  const areTransactionsValid = (transactions: TransactionForTable[]) => {
    const anyTransactionFilled = transactions.some(isTransactionFilled);

    const allFilledTransactionsValid = transactions.every(
      (transaction) =>
        !isTransactionFilled(transaction) ||
        areMandatoryFieldsFilled(transaction)
    );

    return anyTransactionFilled && allFilledTransactionsValid;
  };

  return (
    <>
      <Formik
        initialValues={{
          transactions: initialTransactions,
          reverse: false,
        }}
        onSubmit={onSubmit}
        validateOnChange
        validationSchema={manuallyTransactionsSchema(
          lockDateData?.accounting_lock_date
        )}
      >
        {({
          submitForm,
          values: { transactions },
          isSubmitting,
          isValid,
          setFieldValue,
          errors,
        }) => {
          const transactionsWithLockedDate = transactions.filter(
            (transaction) => {
              return dayjs(transaction.date).isSameOrBefore(
                lockDateData?.accounting_lock_date
              );
            }
          );

          return (
            <Form className="t-m-0 t-w-full t-flex t-flex-col t-flex-1 t-min-h-0">
              <Modal.Body className="t-pb-0 t-flex t-flex-col t-gap-4 t-flex-1 t-min-h-0 sm:t-px-4">
                {transactionsWithLockedDate.length > 0 && (
                  <Banner
                    type="error"
                    title={`${transactionsWithLockedDate.length} transactions are from a locked period and therefore cannot be imported`}
                    description="You can either change the transaction dates manually, update the lock date in settings, or remove transactions from locked period and continue."
                  >
                    <div className="t-flex t-gap-4">
                      <Button
                        type="button"
                        size="small"
                        onClick={() => {
                          const idsToRemove = transactionsWithLockedDate.map(
                            (t) => t.id
                          );

                          setFieldValue(
                            "transactions",
                            transactions.filter(
                              (t) => !idsToRemove.includes(t.id)
                            )
                          );
                        }}
                      >
                        Remove all within locked period
                      </Button>
                      <ConditionalLink to={link("/books/books-settings")}>
                        <Button size="small" customType="text">
                          <div className="t-flex t-gap-1 t-items-center t-text-text-60">
                            <span>Go to settings</span>
                            <ArrowUpRight12 />
                          </div>
                        </Button>
                      </ConditionalLink>
                    </div>
                  </Banner>
                )}

                {isCSVTransactions && (
                  <div className="t-flex t-flex-col t-gap-2">
                    <div className="t-text-subtitle-sm t-text-text-60">
                      Are the uploaded transactions represented like:{" "}
                      <span className="t-text-text-100">
                        expenses= negative numbers & income= positive numbers
                      </span>
                    </div>
                    <RadioGroup.Root
                      defaultValue="YES"
                      onValueChange={(value) => {
                        setFieldValue("reverse", value === "NO" ? true : false);
                      }}
                      className="t-flex t-gap-4"
                    >
                      <RadioGroup.Item value="YES" className="t-w-max">
                        Yes
                      </RadioGroup.Item>
                      <RadioGroup.Item value="NO" className="t-w-max">
                        No, reverse signs
                      </RadioGroup.Item>
                    </RadioGroup.Root>
                  </div>
                )}

                <TransactionTable
                  isCSVTransactions={isCSVTransactions}
                  currency={currency}
                  isNonUSD={isNonUSD}
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                {onBack && (
                  <Button onClick={onBack} type="reset">
                    Back
                  </Button>
                )}
                <ConditionalToolTip
                  condition={isValid ? "" : "Please add all mandatory fields"}
                >
                  <span>
                    <Button
                      customType="primary"
                      type="submit"
                      onClick={submitForm}
                      disabled={
                        transactions.length === 0 ||
                        isSubmitting ||
                        !isValid ||
                        !areTransactionsValid(transactions)
                      }
                      isLoading={isSubmitting}
                    >
                      Add Transactions
                    </Button>
                  </span>
                </ConditionalToolTip>
              </Modal.FooterButtonGroup>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
